import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarDays,
  CheckCircle2,
  LockKeyhole,
  PlayCircle,
  Plus,
  RefreshCw,
  Settings2,
  UnlockKeyhole,
} from 'lucide-react';
import { fixedAssetsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import AssetPageHeader from '../../components/assets/AssetPageHeader';

const mappingLabels = {
  asset_clearing_account_id: 'Fixed Asset Clearing',
  cwip_account_id: 'Fixed Asset CWIP',
  trade_payables_account_id: 'Trade Payables',
  depreciation_expense_account_id: 'Depreciation Expense',
  repairs_maintenance_account_id: 'Repairs & Maintenance',
  gain_on_disposal_account_id: 'Gain on Disposal',
  loss_on_disposal_account_id: 'Loss on Disposal',
  impairment_expense_account_id: 'Impairment Expense',
  accumulated_impairment_account_id: 'Accumulated Impairment',
  asset_sale_receivable_account_id: 'Asset Disposal Receivable',
  opening_balance_clearing_account_id: 'Opening Balance Clearing',
  revaluation_surplus_account_id: 'Revaluation Surplus',
  revaluation_loss_account_id: 'Revaluation Loss',
  recoverable_tax_account_id: 'Recoverable Input Tax',
  accumulated_amortization_account_id: 'Accumulated Amortization',
  amortization_expense_account_id: 'Amortization Expense',
};

const defaults = {
  base_currency: 'LKR',
  currency_precision: 2,
  capitalization_threshold: 0,
  asset_number_format: 'AST-{PREFIX}-{SEQ:06d}',
  default_depreciation_method: 'straight_line',
  default_depreciation_frequency: 'monthly',
  default_depreciation_convention: 'following_month',
  default_useful_life_months: 60,
  default_residual_value_method: 'percentage',
  default_residual_value_percentage: 0,
  physical_verification_frequency_days: 365,
  warranty_alert_days: 30,
  maintenance_alert_days: 14,
  require_financial_period: true,
  require_invoice_before_capitalization: true,
  require_asset_approval: true,
  auto_create_schedule_on_capitalization: true,
  enable_components: true,
  enable_cwip: true,
  enable_impairment: true,
  enable_revaluation: false,
  enable_intangible_assets: true,
  enable_stocktake: true,
  enable_qr_codes: true,
  assignment_acknowledgement_required: true,
  account_mappings: {},
  approval_rules: { segregation_of_duties: true },
  create_default_categories: true,
};

function currentFinancialYear() {
  const today = new Date();
  const year = today.getFullYear();
  const startsThisYear = today.getMonth() >= 3;
  const startYear = startsThisYear ? year : year - 1;
  const endYear = startYear + 1;
  return {
    name: `Financial Year ${startYear}/${endYear}`,
    start_date: `${startYear}-04-01`,
    end_date: `${endYear}-03-31`,
    is_closed: false,
  };
}

export default function AssetSettings() {
  const [status, setStatus] = useState(null);
  const [suggestions, setSuggestions] = useState({ all_accounts: [], suggestions: {} });
  const [form, setForm] = useState(defaults);
  const [periods, setPeriods] = useState([]);
  const [periodForm, setPeriodForm] = useState(currentFinancialYear());
  const [saving, setSaving] = useState(false);
  const [periodSaving, setPeriodSaving] = useState(false);

  const load = async () => {
    try {
      const [statusResponse, accountResponse, periodsResponse] = await Promise.all([
        fixedAssetsAPI.setup.status(),
        fixedAssetsAPI.setup.accountSuggestions(),
        fixedAssetsAPI.setup.periods(),
      ]);
      setStatus(statusResponse.data);
      setSuggestions(accountResponse.data);
      setPeriods(periodsResponse.data || []);
      const saved = statusResponse.data.settings || {};
      const suggested = Object.fromEntries(
        Object.entries(accountResponse.data.suggestions || {})
          .filter(([, value]) => value?.id)
          .map(([key, value]) => [key, value.id]),
      );
      setForm({
        ...defaults,
        ...saved,
        account_mappings: { ...suggested, ...(saved.account_mappings || {}) },
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load Fixed Asset setup');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const accounts = useMemo(
    () => (suggestions.all_accounts || []).filter((account) => !account.is_header && account.is_active !== false),
    [suggestions],
  );
  const openPeriods = useMemo(() => periods.filter((period) => !period.is_closed), [periods]);
  const requiresOpenPeriod = Boolean(form.require_financial_period);
  const activationBlocked = !status?.setup_complete && requiresOpenPeriod && openPeriods.length === 0;

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const createPeriod = async () => {
    if (!periodForm.name.trim() || !periodForm.start_date || !periodForm.end_date) {
      toast.error('Enter the period name, start date and end date');
      return;
    }
    try {
      setPeriodSaving(true);
      await fixedAssetsAPI.setup.createPeriod({ ...periodForm, name: periodForm.name.trim() });
      toast.success('Financial period created and opened');
      setPeriodForm(currentFinancialYear());
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create financial period');
    } finally {
      setPeriodSaving(false);
    }
  };

  const changePeriodState = async (period) => {
    const action = period.is_closed ? 'reopen' : 'close';
    const confirmed = window.confirm(
      period.is_closed
        ? `Reopen ${period.name}? New postings will be allowed inside this period.`
        : `Close ${period.name}? New postings inside this date range will be blocked.`,
    );
    if (!confirmed) return;
    try {
      if (period.is_closed) await fixedAssetsAPI.setup.reopenPeriod(period.id);
      else await fixedAssetsAPI.setup.closePeriod(period.id);
      toast.success(`Financial period ${action}ed`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${action} financial period`);
    }
  };

  const save = async () => {
    if (activationBlocked) {
      toast.error('Create an open financial period in section 1 before activating Fixed Assets');
      return;
    }
    try {
      setSaving(true);
      if (status?.setup_complete) {
        await fixedAssetsAPI.setup.updateSettings(form);
        toast.success('Asset settings updated');
      } else {
        await fixedAssetsAPI.setup.initialize(form);
        toast.success('Fixed Asset Module activated');
      }
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup validation failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <AssetPageHeader
        title="Fixed Asset Setup"
        description="Configure shared financial periods, accounting mappings, depreciation policies and operational controls before activation."
        actions={(
          <Button variant="outline" onClick={load}>
            <RefreshCw className="mr-2 h-4 w-4" />Refresh
          </Button>
        )}
      />

      {status?.setup_complete ? (
        <Alert className="border-emerald-200 bg-emerald-50">
          <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          <AlertTitle>Module active</AlertTitle>
          <AlertDescription>Fixed Asset financial workflows are enabled for this company.</AlertDescription>
        </Alert>
      ) : (
        <Alert>
          <Settings2 className="h-5 w-5" />
          <AlertTitle>Setup required</AlertTitle>
          <AlertDescription>Complete the sections below. Activation itself does not create a journal entry.</AlertDescription>
        </Alert>
      )}

      <Card className={activationBlocked ? 'border-amber-300' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />1. Financial periods
          </CardTitle>
          <CardDescription>
            Financial periods are shared by the General Ledger and Fixed Assets. An open period must cover every posting date.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {activationBlocked && (
            <Alert className="border-amber-300 bg-amber-50">
              <CalendarDays className="h-5 w-5 text-amber-700" />
              <AlertTitle>Create an open period before activation</AlertTitle>
              <AlertDescription>
                Use the fields below. The suggested dates follow URBNSW&apos;s April-to-March financial year.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-end">
            <div>
              <Label>Period name</Label>
              <Input
                value={periodForm.name}
                onChange={(event) => setPeriodForm({ ...periodForm, name: event.target.value })}
                placeholder="Financial Year 2026/2027"
              />
            </div>
            <div>
              <Label>Start date</Label>
              <Input
                type="date"
                value={periodForm.start_date}
                onChange={(event) => setPeriodForm({ ...periodForm, start_date: event.target.value })}
              />
            </div>
            <div>
              <Label>End date</Label>
              <Input
                type="date"
                value={periodForm.end_date}
                onChange={(event) => setPeriodForm({ ...periodForm, end_date: event.target.value })}
              />
            </div>
            <Button onClick={createPeriod} disabled={periodSaving}>
              <Plus className="mr-2 h-4 w-4" />{periodSaving ? 'Creating…' : 'Create period'}
            </Button>
          </div>

          <div className="space-y-2">
            {periods.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-slate-500">
                No financial periods have been created.
              </div>
            ) : periods.map((period) => (
              <div key={period.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <strong>{period.name}</strong>
                    <Badge className={period.is_closed ? 'bg-slate-100 text-slate-700 hover:bg-slate-100' : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'}>
                      {period.is_closed ? 'Closed' : 'Open'}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{period.start_date} to {period.end_date}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => changePeriodState(period)}>
                  {period.is_closed ? <UnlockKeyhole className="mr-2 h-4 w-4" /> : <LockKeyhole className="mr-2 h-4 w-4" />}
                  {period.is_closed ? 'Reopen' : 'Close'}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>2. General and depreciation policy</CardTitle>
            <CardDescription>Company-level defaults; asset categories may override authorized fields.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div><Label>Base currency</Label><Input value={form.base_currency || ''} onChange={(event) => set('base_currency', event.target.value.toUpperCase())} /></div>
            <div><Label>Capitalization threshold</Label><Input type="number" value={form.capitalization_threshold || 0} onChange={(event) => set('capitalization_threshold', Number(event.target.value))} /></div>
            <div><Label>Asset number format</Label><Input value={form.asset_number_format || ''} onChange={(event) => set('asset_number_format', event.target.value)} /></div>
            <div><Label>Default useful life (months)</Label><Input type="number" value={form.default_useful_life_months || 60} onChange={(event) => set('default_useful_life_months', Number(event.target.value))} /></div>
            <div>
              <Label>Depreciation method</Label>
              <Select value={form.default_depreciation_method} onValueChange={(value) => set('default_depreciation_method', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['straight_line', 'reducing_balance', 'double_declining_balance', 'units_of_production', 'manual', 'none'].map((value) => <SelectItem key={value} value={value}>{value.replaceAll('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Convention</Label>
              <Select value={form.default_depreciation_convention} onValueChange={(value) => set('default_depreciation_convention', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['daily_prorata', 'full_month_in_service', 'following_month', 'half_month', 'full_year', 'half_year', 'manual_start_date'].map((value) => <SelectItem key={value} value={value}>{value.replaceAll('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[
              ['require_financial_period', 'Require open financial period'],
              ['require_invoice_before_capitalization', 'Require invoice before capitalization'],
              ['require_asset_approval', 'Require asset approval'],
              ['auto_create_schedule_on_capitalization', 'Auto-create depreciation schedule'],
              ['enable_components', 'Enable components'],
              ['enable_cwip', 'Enable CWIP'],
              ['enable_impairment', 'Enable impairment'],
              ['enable_revaluation', 'Enable revaluation'],
              ['enable_stocktake', 'Enable stocktakes'],
              ['enable_qr_codes', 'Enable QR labels'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between rounded-lg border p-3">
                <Label>{label}</Label>
                <Switch checked={Boolean(form[key])} onCheckedChange={(value) => set(key, value)} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>3. Chart of Accounts mappings</CardTitle>
            <CardDescription>Only active transactional accounts are listed. Header accounts are never accepted.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {Object.entries(mappingLabels).map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Select
                  value={form.account_mappings?.[key] || ''}
                  onValueChange={(value) => setForm({
                    ...form,
                    account_mappings: { ...(form.account_mappings || {}), [key]: value },
                  })}
                >
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{accounts.map((account) => <SelectItem key={account.id} value={account.id}>{account.code} — {account.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>4. Approval and operational controls</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div><Label>Warranty reminder (days)</Label><Input type="number" value={form.warranty_alert_days || 30} onChange={(event) => set('warranty_alert_days', Number(event.target.value))} /></div>
          <div><Label>Maintenance reminder (days)</Label><Input type="number" value={form.maintenance_alert_days || 14} onChange={(event) => set('maintenance_alert_days', Number(event.target.value))} /></div>
          <div><Label>Physical verification interval</Label><Input type="number" value={form.physical_verification_frequency_days || 365} onChange={(event) => set('physical_verification_frequency_days', Number(event.target.value))} /></div>
          <div className="flex items-end">
            <div className="flex w-full items-center justify-between rounded-lg border p-3">
              <Label>Segregation of duties</Label>
              <Switch
                checked={form.approval_rules?.segregation_of_duties !== false}
                onCheckedChange={(value) => setForm({ ...form, approval_rules: { ...(form.approval_rules || {}), segregation_of_duties: value } })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button size="lg" onClick={save} disabled={saving || activationBlocked}>
          <PlayCircle className="mr-2 h-5 w-5" />
          {saving ? 'Validating…' : (status?.setup_complete ? 'Save settings' : 'Validate and activate module')}
        </Button>
      </div>
    </div>
  );
}
